import { useIsSuperAdmin } from "@/core/auth/hooks";
import { Badge } from "@/modules/shadcn/ui/badge";
import { Button } from "@/modules/shadcn/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modules/shadcn/ui/dialog";
import { Spinner } from "@/modules/shadcn/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/modules/shadcn/ui/table";
import type { Device } from "@repo/schema";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  useGetDevicesQuery,
  useUnassignDeviceMutation,
} from "../services/deviceApi";
import { formatDate } from "@/core/i18n";

/**
 * The organization's fleet.
 *
 * "Online" here is derived from the device's last heartbeat, which is the same
 * signal the relay edge uses to decide whether to proxy at all — so a device
 * shown as offline is exactly one that would answer DEVICE_OFFLINE.
 *
 * Revoking is gated on `isSuperAdmin` rather than on the membership role,
 * because that is what the endpoint behind it checks. Gating on OWNER/ADMIN
 * hides the button from the super admins who may actually use it — their
 * session carries `SUPER_ADMIN` as its role in orgs they do not belong to —
 * while showing it to org owners whose every click would 403.
 */
export const DeviceList = () => {
  const { t } = useTranslation(["device", "common"]);
  const isSuperAdmin = useIsSuperAdmin();
  const { data, isLoading } = useGetDevicesQuery();
  const [unassignDevice, { isLoading: isUnassigning }] = useUnassignDeviceMutation();
  const [pending, setPending] = useState<Device | null>(null);

  const devices = data?.devices ?? [];

  const formatLastSeen = (lastSeenAt: string | null): string =>
    lastSeenAt ? formatDate(lastSeenAt) : t("list.never");

  const handleRevoke = async () => {
    if (!pending) {
      return;
    }

    try {
      await unassignDevice({ deviceId: pending.id }).unwrap();
      toast.success(t("list.revoked"));
      setPending(null);
    } catch {
      toast.error(t("list.revokeFailed"));
    }
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h5 className="text-xl font-semibold">{t("list.title")}</h5>
        <p className="text-sm text-black/45">
          {t("list.subtitle")}
        </p>
      </div>

      {devices.length === 0 ? (
        <p className="text-sm text-black/45">
          {t("list.empty")}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("columns.device")}</TableHead>
              <TableHead>{t("columns.cameras")}</TableHead>
              <TableHead>{t("columns.status")}</TableHead>
              <TableHead>{t("columns.firmware")}</TableHead>
              <TableHead>{t("columns.lastSeen")}</TableHead>
              {isSuperAdmin && <TableHead />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {devices.map((device) => (
              <TableRow key={device.id}>
                <TableCell className="font-mono text-xs">
                  {device.deviceId}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {device.cameras.length === 0 ? (
                    <span className="text-black/45">-</span>
                  ) : (
                    device.cameras.map((camera) => (
                      <div key={camera.mxid}>
                        {camera.mxid}
                        {camera.name && (
                          <span className="ml-1 text-black/45">{camera.name}</span>
                        )}
                      </div>
                    ))
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={device.online ? "default" : "secondary"}>
                    {device.online ? t("list.online") : t("list.offline")}
                  </Badge>
                </TableCell>
                <TableCell>{device.firmwareVersion ?? "-"}</TableCell>
                <TableCell>{formatLastSeen(device.lastSeenAt)}</TableCell>
                {isSuperAdmin && (
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={isUnassigning}
                      onClick={() => setPending(device)}
                    >
                      {t("list.revoke")}
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog
        open={pending !== null}
        onOpenChange={(open) => !isUnassigning && !open && setPending(null)}
      >
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle className="text-base font-bold">
              {t("list.revokeDialog.title")}
            </DialogTitle>
            <DialogDescription>
              <Trans
                t={t}
                i18nKey="list.revokeDialog.description"
                values={{ deviceId: pending?.deviceId }}
                components={{ strong: <strong className="font-mono" /> }}
              />
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPending(null)}
              disabled={isUnassigning}
            >
              {t("common:actions.cancel")}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleRevoke}
              disabled={isUnassigning}
            >
              {isUnassigning ? t("list.revoking") : t("list.revoke")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
